import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, mergeProps as _mergeProps, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = { class: "flex justify-between items-center mb-0 pt-2 pl-2 pr-2" }
const _hoisted_3 = { class: "search-bar" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = ["onClick"]

import { ref, onMounted, watch } from 'vue';
import { getCableSpecs, createCableSpecs, updateCableSpecs, deleteCableSpecs } from '@/services/copperPrice.service';
import { CableSpecs } from '@/types/copperPrice.interface';

interface HeaderItem {
  title: string;
  align: 'start' | 'end' | 'center';
  key: string;
  sortable?: boolean;
  width?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CableSpecs',
  setup(__props) {

const search = ref('');
const editDialog = ref(false);
const deleteDialog = ref(false);
const isLoading = ref(false);
const editedItem = ref<CableSpecs>({
  cable: '',
  copper_weight: 0,
  termination_fee: 0,
});
const editedIndex = ref(-1);
const itemToDelete = ref<string | null>(null);

const headers = ref<HeaderItem[]>([
  { title: 'Cable', align: 'start', key: 'cable' },
  { title: 'Copper Weight (lbs/kft)', align: 'end', key: 'copper_weight' },
  { title: 'Termination Fee ($)', align: 'end', key: 'termination_fee' },
  { title: 'Actions', key: 'actions', sortable: false, width: '100px', align: 'center' },
]);

const cableSpecs = ref<CableSpecs[]>([]);

const errors = ref({
  cable: '',
  copper_weight: '',
  termination_fee: '',
});

const touchedFields = ref({
  cable: false,
  copper_weight: false,
  termination_fee: false,
});

const isFormValid = ref(false);
const hasErrors = ref(false);

watch(editDialog, (newVal) => {
  if (newVal === true) {
    validateAllFields();
  }
});

watch(
  () => editedItem.value,
  () => {
    validateAllFields();
  },
  { deep: true },
);

const validateAllFields = () => {
  if (touchedFields.value.cable) validateCable(editedItem.value.cable);
  if (touchedFields.value.copper_weight) validateCopperWeight(editedItem.value.copper_weight);
  if (touchedFields.value.termination_fee) validateTerminationFee(editedItem.value.termination_fee);
};

const validateCable = (value: string) => {
  if (!touchedFields.value.cable) return;

  if (!value || value.trim() === '') {
    errors.value.cable = 'Cable type is required';
  } else {
    errors.value.cable = '';
  }
  validateForm();
};

const validateCopperWeight = (value: number | string | null) => {
  if (!touchedFields.value.copper_weight) return;

  if (value === null || value === undefined || value === '') {
    errors.value.copper_weight = 'Copper weight is required';
  } else {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) {
      errors.value.copper_weight = 'Copper weight must be a valid number';
    } else if (numValue < 0) {
      errors.value.copper_weight = 'Copper weight must be 0 or positive';
    } else {
      errors.value.copper_weight = '';
    }
  }
  validateForm();
};

const validateTerminationFee = (value: number | string | null) => {
  if (!touchedFields.value.termination_fee) return;

  if (value === null || value === undefined || value === '') {
    errors.value.termination_fee = 'Termination fee is required';
  } else {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) {
      errors.value.termination_fee = 'Termination fee must be a valid number';
    } else if (numValue < 0) {
      errors.value.termination_fee = 'Termination fee must be 0 or positive';
    } else {
      errors.value.termination_fee = '';
    }
  }
  validateForm();
};

const validateForm = () => {
  const hasValidationErrors = !!errors.value.cable || !!errors.value.copper_weight || !!errors.value.termination_fee;

  const isFilled =
    !!editedItem.value.cable?.trim() &&
    editedItem.value.copper_weight !== null &&
    editedItem.value.copper_weight !== undefined &&
    !isNaN(Number(editedItem.value.copper_weight)) &&
    editedItem.value.termination_fee !== null &&
    editedItem.value.termination_fee !== undefined &&
    !isNaN(Number(editedItem.value.termination_fee));

  hasErrors.value = hasValidationErrors;
  isFormValid.value = !hasValidationErrors && isFilled;
};

const fetchCableSpecs = async () => {
  try {
    isLoading.value = true;
    const response = await getCableSpecs();
    cableSpecs.value = response.data;
  } catch (error) {
    console.error('Error fetching cable specifications:', error);
  } finally {
    isLoading.value = false;
  }
};

const openEditDialog = (item: CableSpecs) => {
  editedIndex.value = cableSpecs.value.indexOf(item);
  editedItem.value = {
    ...item,
    copper_weight: typeof item.copper_weight === 'string' ? parseFloat(item.copper_weight) : item.copper_weight,
    termination_fee: typeof item.termination_fee === 'string' ? parseFloat(item.termination_fee) : item.termination_fee,
  };
  editDialog.value = true;
};

const handleDeleteCableSpecs = async (id: string) => {
  itemToDelete.value = id;
  deleteDialog.value = true;
};

const confirmDelete = async () => {
  if (!itemToDelete.value) return;

  try {
    await deleteCableSpecs(itemToDelete.value);
    cableSpecs.value = cableSpecs.value.filter((item) => item.id !== itemToDelete.value);
    deleteDialog.value = false;
    itemToDelete.value = null;
  } catch (error) {
    console.error('Error deleting cable specification:', error);
  }
};

const closeEditDialog = () => {
  editDialog.value = false;
  editedItem.value = {
    cable: '',
    copper_weight: null as unknown as number,
    termination_fee: null as unknown as number,
  };
  editedIndex.value = -1;
  errors.value = {
    cable: '',
    copper_weight: '',
    termination_fee: '',
  };
  touchedFields.value = {
    cable: false,
    copper_weight: false,
    termination_fee: false,
  };
  isFormValid.value = false;
  hasErrors.value = false;
};

const saveEdit = async () => {
  if (!isFormValid.value) return;

  try {
    if (editedIndex.value > -1) {
      const item = cableSpecs.value[editedIndex.value];
      if (item.id) {
        const updateData = {
          cable: editedItem.value.cable,
          copper_weight: Number(editedItem.value.copper_weight),
          termination_fee: Number(editedItem.value.termination_fee),
        };
        await updateCableSpecs(item.id, updateData);
        Object.assign(cableSpecs.value[editedIndex.value], updateData);
      }
    } else {
      const newData = {
        cable: editedItem.value.cable,
        copper_weight: Number(editedItem.value.copper_weight),
        termination_fee: Number(editedItem.value.termination_fee),
      };
      const response = await createCableSpecs(newData);
      cableSpecs.value.push(response.data);
    }
    closeEditDialog();
  } catch (error) {
    console.error('Error saving cable specification:', error);
  }
};

const addCableSpecs = () => {
  editedIndex.value = -1;
  editedItem.value = {
    cable: '',
    copper_weight: null as unknown as number,
    termination_fee: null as unknown as number,
  };
  editDialog.value = true;
};

onMounted(() => {
  fetchCableSpecs();
});

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[11] || (_cache[11] = _createElementVNode("h2", { class: "text-2xl font-semibold" }, "Cable Specifications", -1)),
      _createVNode(_component_v_btn, {
        class: "btn-orange",
        onClick: addCableSpecs
      }, {
        default: _withCtx(() => _cache[10] || (_cache[10] = [
          _createTextVNode("Add Cable Specification")
        ])),
        _: 1
      })
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_component_v_text_field, {
        modelValue: search.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
        label: "Search by cable type...",
        "single-line": "",
        "hide-details": "",
        class: _normalizeClass(['search-bar-input']),
        style: {"width":"300px","background-color":"white"},
        variant: "outlined",
        density: "compact"
      }, null, 8, ["modelValue"])
    ]),
    _createVNode(_component_v_data_table, {
      headers: headers.value,
      items: cableSpecs.value,
      search: search.value,
      "items-per-page": 10,
      class: "elevation-1",
      density: "compact",
      loading: isLoading.value
    }, {
      "item.actions": _withCtx(({ item }) => [
        _createVNode(_component_v_tooltip, {
          text: "Edit",
          location: "top"
        }, {
          activator: _withCtx(({ props }) => [
            _createElementVNode("span", _mergeProps({ class: "btn-icon-edit" }, props, {
              onClick: ($event: any) => (openEditDialog(item))
            }), [
              _createVNode(_component_v_icon, { color: "grey" }, {
                default: _withCtx(() => _cache[12] || (_cache[12] = [
                  _createTextVNode("mdi-pencil")
                ])),
                _: 1
              })
            ], 16, _hoisted_4),
            _createVNode(_component_v_tooltip, {
              text: "Delete",
              location: "top"
            }, {
              activator: _withCtx(({ props }) => [
                _createElementVNode("span", _mergeProps({ class: "btn-icon-delete" }, props, {
                  onClick: ($event: any) => (handleDeleteCableSpecs(item.id))
                }), [
                  _createVNode(_component_v_icon, { color: "red" }, {
                    default: _withCtx(() => _cache[13] || (_cache[13] = [
                      _createTextVNode("mdi-delete")
                    ])),
                    _: 1
                  })
                ], 16, _hoisted_5)
              ]),
              _: 2
            }, 1024)
          ]),
          _: 2
        }, 1024)
      ]),
      _: 1
    }, 8, ["headers", "items", "search", "loading"]),
    _createVNode(_component_v_dialog, {
      modelValue: editDialog.value,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((editDialog).value = $event)),
      "max-width": "500px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "text-h5 modal-title" }, {
              default: _withCtx(() => _cache[14] || (_cache[14] = [
                _createTextVNode("Edit Cable Specifications")
              ])),
              _: 1
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_container, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_row, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_col, { cols: "12" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_text_field, {
                              modelValue: editedItem.value.cable,
                              "onUpdate:modelValue": [
                                _cache[1] || (_cache[1] = ($event: any) => ((editedItem.value.cable) = $event)),
                                validateCable
                              ],
                              label: editedIndex.value > -1 ? 'Cable Type (Read Only on editing)' : 'Cable Type',
                              readonly: editedIndex.value > -1,
                              rules: [(v) => !!v || 'Cable type is required'],
                              variant: "outlined",
                              density: "compact",
                              "error-messages": errors.value.cable,
                              onBlur: _cache[2] || (_cache[2] = ($event: any) => (touchedFields.value.cable = true))
                            }, null, 8, ["modelValue", "label", "readonly", "rules", "error-messages"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_col, { cols: "12" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_text_field, {
                              modelValue: editedItem.value.copper_weight,
                              "onUpdate:modelValue": [
                                _cache[3] || (_cache[3] = ($event: any) => ((editedItem.value.copper_weight) = $event)),
                                validateCopperWeight
                              ],
                              label: "Copper Weight (lbs/kft)",
                              type: "number",
                              rules: [
                  (v) => !!v || 'Copper weight is required',
                  (v) => v >= 0 || 'Copper weight must be 0 or positive',
                ],
                              variant: "outlined",
                              density: "compact",
                              "error-messages": errors.value.copper_weight,
                              onBlur: _cache[4] || (_cache[4] = ($event: any) => (touchedFields.value.copper_weight = true))
                            }, null, 8, ["modelValue", "rules", "error-messages"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_col, { cols: "12" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_text_field, {
                              modelValue: editedItem.value.termination_fee,
                              "onUpdate:modelValue": [
                                _cache[5] || (_cache[5] = ($event: any) => ((editedItem.value.termination_fee) = $event)),
                                validateTerminationFee
                              ],
                              label: "Termination Fee ($)",
                              type: "number",
                              rules: [
                  (v) => !!v || 'Termination fee is required',
                  (v) => v >= 0 || 'Termination fee must be 0 or positive',
                ],
                              variant: "outlined",
                              density: "compact",
                              "error-messages": errors.value.termination_fee,
                              onBlur: _cache[6] || (_cache[6] = ($event: any) => (touchedFields.value.termination_fee = true))
                            }, null, 8, ["modelValue", "rules", "error-messages"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, { class: "px-4 pb-4" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  class: "btn-cancel",
                  variant: "text",
                  onClick: closeEditDialog
                }, {
                  default: _withCtx(() => _cache[15] || (_cache[15] = [
                    _createTextVNode("Cancel")
                  ])),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  class: "btn-save",
                  variant: "text",
                  onClick: saveEdit,
                  disabled: hasErrors.value || !isFormValid.value
                }, {
                  default: _withCtx(() => _cache[16] || (_cache[16] = [
                    _createTextVNode("Save")
                  ])),
                  _: 1
                }, 8, ["disabled"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_dialog, {
      modelValue: deleteDialog.value,
      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((deleteDialog).value = $event)),
      "max-width": "400px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "text-h5 modal-title" }, {
              default: _withCtx(() => _cache[17] || (_cache[17] = [
                _createTextVNode("Confirm Delete")
              ])),
              _: 1
            }),
            _createVNode(_component_v_card_text, { class: "pa-4" }, {
              default: _withCtx(() => _cache[18] || (_cache[18] = [
                _createTextVNode(" Are you sure you want to delete this cable specification? ")
              ])),
              _: 1
            }),
            _createVNode(_component_v_card_actions, { class: "px-4 pb-4" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  class: "btn-cancel",
                  variant: "text",
                  onClick: _cache[8] || (_cache[8] = ($event: any) => (deleteDialog.value = false))
                }, {
                  default: _withCtx(() => _cache[19] || (_cache[19] = [
                    _createTextVNode("Cancel")
                  ])),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  class: "btn-delete",
                  variant: "text",
                  onClick: confirmDelete
                }, {
                  default: _withCtx(() => _cache[20] || (_cache[20] = [
                    _createTextVNode("Delete")
                  ])),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})